import { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import React from 'react';
import Pagination from '@/components/DataTable/Pagination';
import Skeleton from '@/components/Skeleton/Skeleton';
import ZINDEXES from '@/constants/zIndexes';

const DataTable = ({
    columns,
    data,
    maxHeight = 400,
    onSortChange,
    sort,
    renderActions,
    expandable = false,
    renderExpandedRow,
    getRowClass,
    mode = 'client',
    page,
    pageSize,
    pageSizes,
    total,
    onPageChange,
    onPageSizeChange,
    loading,
    error,
}) => {
    const displayedData = useMemo(() => {
        if (mode === 'client') {
            let sortedData = [...data];

            if (sort.accessor) {
                sortedData.sort((a, b) => {
                    const aValue = a[sort.accessor];
                    const bValue = b[sort.accessor];

                    if (aValue < bValue)
                        return sort.direction === 'asc' ? -1 : 1;
                    if (aValue > bValue)
                        return sort.direction === 'asc' ? 1 : -1;
                    return 0;
                });
            }

            const start = (page - 1) * pageSize;
            const end = start + pageSize;

            return sortedData.slice(start, end);
        }

        return data;
    }, [data, mode, sort, page, pageSize]);

    const totalDataLength = useMemo(() => {
        return mode === 'client' ? data.length : total;
    }, [data, mode, total]);

    const [expandedRows, setExpandedRows] = useState([]);

    const toggleExpand = (rowIndex) => {
        setExpandedRows((prev) =>
            prev.includes(rowIndex)
                ? prev.filter((i) => i !== rowIndex)
                : [...prev, rowIndex]
        );
    };

    const handleSort = (accessor) => {
        if (!onSortChange) return;
        const direction =
            sort.accessor === accessor && sort.direction === 'asc'
                ? 'desc'
                : 'asc';

        onSortChange({ accessor, direction });
    };

    const renderCell = (col, row) => {
        return col.render?.(row) ?? row[col.accessor] ?? '---';
    };

    const SkeletonRow = ({ columnCount, expandable, renderActions }) => (
        <tr className="border-t">
            {expandable && (
                <td className="px-6 py-4">
                    <Skeleton width="1rem" />
                </td>
            )}

            {Array.from({ length: columnCount }).map((_, i) => (
                <td key={i} className="px-6 py-4">
                    <Skeleton />
                </td>
            ))}

            {renderActions && (
                <td className="px-6 py-4">
                    <Skeleton width="4rem" />
                </td>
            )}
        </tr>
    );

    const DataRow = ({
        row,
        rowIndex,
        columns,
        expandable,
        expandedRows,
        toggleExpand,
        renderCell,
        renderActions,
        renderExpandedRow,
        getRowClass,
    }) => (
        <React.Fragment>
            <tr className={`border-t ${getRowClass ? getRowClass(row) : ''}`}>
                {expandable && (
                    <td className="text-center px-6 py-4">
                        <button onClick={() => toggleExpand(rowIndex)}>
                            {expandedRows.includes(rowIndex) ? (
                                <ChevronUp size={16} />
                            ) : (
                                <ChevronDown size={16} />
                            )}
                        </button>
                    </td>
                )}
                {columns.map((col) => (
                    <td key={col.label || col.accessor} className="px-6 py-4">
                        {renderCell(col, row)}
                    </td>
                ))}
                {renderActions && (
                    <td className="px-6 py-4">{renderActions(row)}</td>
                )}
            </tr>

            {expandable && expandedRows.includes(rowIndex) && (
                <tr className="bg-gray-50">
                    <td
                        colSpan={columns.length + 1 + (renderActions ? 1 : 0)}
                        className="px-6 py-4"
                    >
                        {renderExpandedRow
                            ? renderExpandedRow(row)
                            : 'Expanded content'}
                    </td>
                </tr>
            )}
        </React.Fragment>
    );

    const TableBody = ({
        loading,
        pageSize,
        displayedData,
        columns,
        expandable,
        expandedRows,
        toggleExpand,
        renderCell,
        renderActions,
        renderExpandedRow,
        getRowClass,
        error,
        emptyMessage = 'No data available', // optional prop
    }) => {
        const totalColumns =
            columns.length + (expandable ? 1 : 0) + (renderActions ? 1 : 0);

        if (loading) {
            return (
                <tbody>
                    {Array.from({ length: pageSize || 5 }).map((_, i) => (
                        <SkeletonRow
                            key={i}
                            columnCount={columns.length}
                            expandable={expandable}
                            renderActions={renderActions}
                        />
                    ))}
                </tbody>
            );
        }

        if (error || displayedData.length === 0) {
            return (
                <tbody>
                    <tr className="border-t">
                        <td
                            colSpan={totalColumns}
                            className="px-6 py-4 text-center text-gray-500"
                        >
                            {error || emptyMessage}
                        </td>
                    </tr>
                </tbody>
            );
        }

        return (
            <tbody>
                {displayedData.map((row, rowIndex) => (
                    <DataRow
                        key={row.id || rowIndex}
                        row={row}
                        rowIndex={rowIndex}
                        columns={columns}
                        expandable={expandable}
                        expandedRows={expandedRows}
                        toggleExpand={toggleExpand}
                        renderCell={renderCell}
                        renderActions={renderActions}
                        renderExpandedRow={renderExpandedRow}
                        getRowClass={getRowClass}
                    />
                ))}
            </tbody>
        );
    };

    return (
        <div
            className="w-full border rounded-lg shadow-sm flex flex-col"
            style={{ maxHeight: `${maxHeight}px` }}
        >
            <div className="flex-1 overflow-y-auto">
                <table className="min-w-full table-auto text-sm text-left">
                    <thead
                        className="bg-gray-100 text-secondary-500 sticky top-0"
                        style={{ zIndex: ZINDEXES.TABLE_HEADER }}
                    >
                        <tr>
                            {expandable && <th className="w-10"></th>}
                            {columns.map((col) => (
                                <th
                                    key={col.label || col.accessor}
                                    className={`px-6 py-3 select-none  ${
                                        col.sortable ? 'cursor-pointer' : ''
                                    }`}
                                    onClick={() =>
                                        col.sortable && handleSort(col.accessor)
                                    }
                                >
                                    <div className="flex items-center gap-1">
                                        {col.label}
                                        {col.sortable && (
                                            <>
                                                {sort.accessor ===
                                                col.accessor ? (
                                                    sort.direction === 'asc' ? (
                                                        <ChevronUp size={16} />
                                                    ) : (
                                                        <ChevronDown
                                                            size={16}
                                                        />
                                                    )
                                                ) : (
                                                    <ChevronDown
                                                        size={16}
                                                        className="opacity-30"
                                                    />
                                                )}
                                            </>
                                        )}
                                    </div>
                                </th>
                            ))}
                            {renderActions && <th className="px-6 py-3"></th>}
                        </tr>
                    </thead>

                    <TableBody
                        loading={loading}
                        pageSize={pageSize}
                        displayedData={displayedData}
                        columns={columns}
                        expandable={expandable}
                        expandedRows={expandedRows}
                        toggleExpand={toggleExpand}
                        renderCell={renderCell}
                        renderActions={renderActions}
                        renderExpandedRow={renderExpandedRow}
                        getRowClass={getRowClass}
                        error={error}
                    />
                </table>
            </div>

            {onPageChange && totalDataLength > 0 && (
                <div className="border-t bg-white">
                    <Pagination
                        page={page}
                        pageSize={pageSize}
                        pageSizes={pageSizes}
                        total={totalDataLength}
                        loading={loading}
                        onPageChange={onPageChange}
                        onPageSizeChange={onPageSizeChange}
                    />
                </div>
            )}
        </div>
    );
};

export default DataTable;
